import type {
  Currency,
  EnabledCanolaMonthCode,
  FuturesMonthCode,
  InstrumentId,
  PairPresetId,
} from "./types";

export type DisplayMetric = "raw-spread" | "ratio" | "log-ratio" | "beta-residual";

export interface PairPreset {
  readonly id: PairPresetId;
  readonly label: string;
  readonly legA: InstrumentId;
  readonly legB: InstrumentId;
  readonly canolaLeg: "legA" | "legB";
  readonly commonCurrency: Currency;
  readonly commonUnit: "metric-tonne";
  readonly primaryDisplayMetric: DisplayMetric;
  readonly primaryStatisticalMetric: DisplayMetric;
  readonly permittedMetrics: readonly DisplayMetric[];
  readonly direction: "positive-is-leg-a-rich";
  readonly enabledCanolaMonths: readonly EnabledCanolaMonthCode[];
  readonly enabledComparatorMonths: readonly FuturesMonthCode[];
  readonly warning?: string;
}
export const ENABLED_CANOLA_MONTHS = ["F", "H", "K", "N", "X"] as const;

export const PAIR_PRESETS: Readonly<Record<PairPresetId, PairPreset>> = {
  "canola-soybean": {
    id: "canola-soybean",
    label: "ICE Canola − CBOT Soybean",
    legA: "ICE_CANOLA",
    legB: "CBOT_SOYBEAN",
    canolaLeg: "legA",
    commonCurrency: "CAD",
    commonUnit: "metric-tonne",
    primaryDisplayMetric: "beta-residual",
    primaryStatisticalMetric: "beta-residual",
    permittedMetrics: ["raw-spread", "ratio", "log-ratio", "beta-residual"],
    direction: "positive-is-leg-a-rich",
    enabledCanolaMonths: ENABLED_CANOLA_MONTHS,
    enabledComparatorMonths: ["F", "H", "K", "N", "X"],
  },
  "canola-soybean-oil": {
    id: "canola-soybean-oil",
    label: "CBOT Soybean Oil − ICE Canola",
    legA: "CBOT_SOYBEAN_OIL",
    legB: "ICE_CANOLA",
    canolaLeg: "legB",
    commonCurrency: "CAD",
    commonUnit: "metric-tonne",
    primaryDisplayMetric: "beta-residual",
    primaryStatisticalMetric: "beta-residual",
    permittedMetrics: ["raw-spread", "ratio", "log-ratio", "beta-residual"],
    direction: "positive-is-leg-a-rich",
    enabledCanolaMonths: ENABLED_CANOLA_MONTHS,
    enabledComparatorMonths: ["F", "H", "K", "N", "Z"],
    warning: "Seed versus processed vegetable oil; raw spread is not a physical parity.",
  },
  "canola-matif-rapeseed": {
    id: "canola-matif-rapeseed",
    label: "MATIF Rapeseed − ICE Canola",
    legA: "MATIF_RAPESEED",
    legB: "ICE_CANOLA",
    canolaLeg: "legB",
    commonCurrency: "CAD",
    commonUnit: "metric-tonne",
    primaryDisplayMetric: "raw-spread",
    primaryStatisticalMetric: "raw-spread",
    permittedMetrics: ["raw-spread", "ratio", "log-ratio", "beta-residual"],
    direction: "positive-is-leg-a-rich",
    enabledCanolaMonths: ENABLED_CANOLA_MONTHS,
    enabledComparatorMonths: ["G", "K", "Q", "X"],
  },
};

export const PAIR_PRESET_LIST = Object.freeze([
  PAIR_PRESETS["canola-matif-rapeseed"],
  PAIR_PRESETS["canola-soybean"],
  PAIR_PRESETS["canola-soybean-oil"],
]) as readonly PairPreset[];

export function getPairPreset(pairId: PairPresetId): PairPreset {
  return PAIR_PRESETS[pairId];
}

export function enabledMonthsForLeg(
  preset: PairPreset,
  leg: "legA" | "legB",
): readonly FuturesMonthCode[] {
  return leg === preset.canolaLeg
    ? preset.enabledCanolaMonths
    : preset.enabledComparatorMonths;
}
